import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dropdown, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

const useColumnSelector = (storageKey) => {
  const { t } = useTranslation();

  const columnsRef = useRef({});
  const renderScheduledRef = useRef(false);
  const [, forceRender] = useState(0);
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  const [hiddenColumns, setHiddenColumns] = useState(() => {
    const raw = localStorage.getItem(`col_sel_${storageKey}`);
    return raw ? JSON.parse(raw) : {};
  });

  useEffect(() => {
    localStorage.setItem(`col_sel_${storageKey}`, JSON.stringify(hiddenColumns));
  }, [hiddenColumns, storageKey]);

  const registerColumn = useCallback((label, readOnly = false) => {
    const existing = columnsRef.current[label];

    if (!existing) {
      columnsRef.current[label] = { readOnly: !!readOnly };
      if (!renderScheduledRef.current) {
        renderScheduledRef.current = true;
        setTimeout(() => {
          renderScheduledRef.current = false;
          forceRender(v => v + 1);
        }, 0);
      }
      return;
    }

    if (readOnly && !existing.readOnly) {
      columnsRef.current[label] = { ...existing, readOnly: true };
      if (!renderScheduledRef.current) {
        renderScheduledRef.current = true;
        setTimeout(() => {
          renderScheduledRef.current = false;
          forceRender(v => v + 1);
        }, 0);
      }
    }
  }, []);

  const toggleColumn = useCallback((label) => {
    if (columnsRef.current[label]?.readOnly) return;

    setHiddenColumns(prev => {
      const next = { ...prev };
      next[label] = !next[label];
      return next;
    });
  }, []);
  const columnFilter = useCallback((label, options) => {
    const readOnly = !!options?.readOnly;
    registerColumn(label, readOnly);
    return !!hiddenColumns[label];
  }, [hiddenColumns, registerColumn]);

  const columnFilterReadOnly = useCallback(
    (label) => columnFilter(label, { readOnly: true }),
    [columnFilter]
  );

  const handleToggle = useCallback((nextShow, event) => {
    if (event && menuRef.current && menuRef.current.contains(event.target)) {
      return;
    }
    setOpen(nextShow);
  }, []);

  const ColumnSelector = () => (
    <>
      <style>{`.form-check-input { accent-color: #5895E9 !important; }
            .form-check-input:checked { background-color: #5895E9 !important; border-color: #5895E9 !important; }
            .form-check-input:focus { box-shadow: 0 0 0 0.2rem rgba(88,149,233,0.25) !important; }`}
      </style>
      <Dropdown show={open} onToggle={handleToggle} autoClose="outside">
        <Dropdown.Toggle variant="link" id="dropdown-column-selector" className="sa-table-icon-btn p-0">
          <div className="sa-table-actions sa-action-m" style={{ paddingTop: "4px" }} title={t('button.SELECT_COLUMNS')}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="column-selector-icon">
              <path d="M9 3V21M15 3V21M5 3H19C20.1046 3 21 3.89543 21 5V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3Z" stroke="#AAAAAA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </Dropdown.Toggle>

        <Dropdown.Menu ref={menuRef} className="p-3" style={{ minWidth: '200px' }}>
          <h6 className="px-0 mb-2">{t('title.SELECT_COLUMNS')}</h6>
          {Object.keys(columnsRef.current).map((label) => {
          const meta = columnsRef.current[label] || {};
          return (
            <Form.Check
              key={label}
              type="checkbox"
              id={`col-check-${label}`}
              label={label}
              checked={!hiddenColumns[label]}
              onChange={() => toggleColumn(label)}
              className="mb-2"
              disabled={!!meta.readOnly}
            />
          );
          })}
        </Dropdown.Menu>
      </Dropdown>
    </>
  );

  return { ColumnSelector, columnFilter, columnFilterReadOnly };
};

export default useColumnSelector;
